import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { applyDemoFilter } from '@/lib/demoFilter';

interface DashboardStats {
  loadsPosted: number;
  activeLoadsOnboard: number;
  loadsOpen: number;
  loadsExpired: number;
  activeTrucks: number;
  isLoading: boolean;
}

interface DashboardFilters {
  timeRange?: 'Current' | '1 Day' | '7 Day' | '30 Day';
  enabled?: boolean;
}

const fetchDashboardStats = async (userId: string, timeRange?: string, isDemoUser: boolean = false) => {
  let userLoadsQuery = supabase
    .from('loads')
    .select(`id, status, rate_amount, created_at, pickup_date, origin_state, destination_state, origin_city, destination_city`)
    .eq('created_by', userId);

  userLoadsQuery = applyDemoFilter(userLoadsQuery, isDemoUser);

  if (timeRange && timeRange !== 'Current') {
    userLoadsQuery = userLoadsQuery.gte('created_at', getTimeRangeFilter(timeRange));
  }

  const { data: userLoads, error: userLoadsError } = await userLoadsQuery;
  if (userLoadsError) throw userLoadsError;

  const today = new Date().toISOString().split('T')[0];

  let platformLoadsQuery = supabase.from('loads').select('*', { count: 'exact', head: true }).eq('status', 'posted').eq('is_public', true);
  platformLoadsQuery = applyDemoFilter(platformLoadsQuery, isDemoUser);

  let platformTrucksQuery = supabase.from('truck_postings').select('*', { count: 'exact', head: true }).in('status', ['active', 'available']).gte('available_to', today);
  platformTrucksQuery = applyDemoFilter(platformTrucksQuery, isDemoUser);

  const [{ count: platformLoadsCount }, { count: platformTrucksCount }] = await Promise.all([
    platformLoadsQuery,
    platformTrucksQuery,
  ]);

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const loads = userLoads || [];

  const postedLoads = loads.filter(load => load.status === 'posted');
  const expiredLoads = loads.filter(load => {
    if (load.status !== 'posted') return false;
    const pickupDate = new Date(load.pickup_date);
    pickupDate.setHours(0, 0, 0, 0);
    return pickupDate < now;
  });
  const openLoads = postedLoads.filter(load => {
    const pickupDate = new Date(load.pickup_date);
    pickupDate.setHours(0, 0, 0, 0);
    return pickupDate >= now;
  });

  return {
    loadsPosted: loads.length,
    activeLoadsOnboard: platformLoadsCount ?? 0,
    loadsOpen: openLoads.length,
    loadsExpired: expiredLoads.length,
    activeTrucks: platformTrucksCount ?? 0,
  };
};

export const useDashboardStats = (filters?: DashboardFilters): DashboardStats => {
  const { user, isDemoUser } = useAuth();

  const isEnabled = (filters?.enabled ?? true) && !!user;

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stats', user?.id, filters?.timeRange, isDemoUser],
    queryFn: () => fetchDashboardStats(user!.id, filters?.timeRange, isDemoUser),
    enabled: isEnabled,
    staleTime: 30_000,
  });

  return {
    loadsPosted: data?.loadsPosted ?? 0,
    activeLoadsOnboard: data?.activeLoadsOnboard ?? 0,
    loadsOpen: data?.loadsOpen ?? 0,
    loadsExpired: data?.loadsExpired ?? 0,
    activeTrucks: data?.activeTrucks ?? 0,
    isLoading,
  };
};

const getTimeRangeFilter = (timeRange: string): string => {
  const now = new Date();
  switch (timeRange) {
    case '1 Day':
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      return today.toISOString();
    case '7 Day':
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      return weekAgo.toISOString();
    case '30 Day':
      const monthAgo = new Date(now);
      monthAgo.setDate(now.getDate() - 30);
      return monthAgo.toISOString();
    case 'Current':
    default:
      return new Date(0).toISOString();
  }
};
